import { usePriceHistory } from '@ui/hooks/price/usePriceHistory';
import { queryClient } from '@ui/lib/reactQuery';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { useTradingViewConfig } from '@ui/services/pyth-benchmark/config/useTradingViewConfig';
import { GetHistoryParams } from '@ui/services/pyth-benchmark/history/getHistory';
import {
  subscribeOnStream,
  unsubscribeFromStream,
} from '@ui/services/pyth-benchmark/stream/streaming';
import { useBenchmarkSymbols } from '@ui/services/pyth-benchmark/symbols/useBenchmarkSymbols';
import {
  isSupportedResolution,
  SupportedResolution,
} from '@ui/services/pyth-benchmark/types';
import { SYMBOL_TO_HERMS_PRICE_ID } from '@ui/services/pyth-hermes/constants';
import { pythHermesLatestPriceQuery } from '@ui/services/pyth-hermes/latest-price/getPythLatestPrice';
import {
  GetPythProHistoryParams,
} from '@ui/services/pyth-pro/history/getHistory';
import { usePythProHistory } from '@ui/services/pyth-pro/history/usePythProHistory';
import { ChainId } from '@ui/types/chain';
import { BareTradePair } from '@ui/types/tradePair';
import { getTokenPriceDisplayDecimals } from '@ui/utils/getTokenPriceDisplayDecimals';
import { getChartTimezone } from '@ui/utils/getUtcOffset';
import { invariant } from '@ui/utils/invariant';
import { objectEntries } from '@ui/utils/objectEntries';
import BigNumber from 'bignumber.js';
import {
  Bar,
  IBasicDataFeed,
  IDatafeedChartApi,
  IDatafeedQuotesApi,
  IExternalDatafeed,
  LibrarySymbolInfo,
  Timezone,
} from 'trading-view';

import { SUPPORTED_RESOLUTION_STRINGS } from '../constants';
import { getPythScheduleInfo } from './scheduleParsers';

// Use it to keep a record of the most recent bar on the chart
const lastBarsCache = new Map<string, Bar>();
// Track whether we've already handled the *initial* firstDataRequest
// for a given (chartId, resolution) combination.
// This lets us customize the very first load range on a chart while still
// respecting the visible window for subsequent reloads/resets or pair switches.
const firstRequestHandled = new Set<string>();
let resetDataFn: (() => void) | null = null;
type LeverUpSymbolInfo = LibrarySymbolInfo & {
  pythProPriceFeedId?: string;
  pythProSymbol?: string;
};
type PythProSymbolConfig = {
  feedId: string;
  symbol: string;
};
const pythProSymbolConfigCache = new Map<string, PythProSymbolConfig>();
/**
  chart-id_Crypto.ETH/USD_#_240
 */
const getBarCacheKey = (ticker: string, resolution: string, chartId: string) =>
  `${chartId}_${ticker}_#_${resolution}`;

const getUniqSubscriptionId = (subscriberUID: string, chartId: string) => {
  return `${chartId}_${subscriberUID}`;
};

const getPythProHistorySymbol = (
  pair: Pick<BareTradePair, 'pairName' | 'ticker'>,
) => {
  return pair.ticker;
};

const getPythProSymbolConfig = (symbolInfo: LeverUpSymbolInfo) => {
  if (symbolInfo.pythProPriceFeedId && symbolInfo.pythProSymbol) {
    return {
      feedId: symbolInfo.pythProPriceFeedId,
      symbol: symbolInfo.pythProSymbol,
    };
  }

  if (!symbolInfo.ticker) {
    return undefined;
  }

  return pythProSymbolConfigCache.get(symbolInfo.ticker);
};

const getPythProSymbolConfigFromPairsCache = ({
  chainId,
  ticker,
}: {
  chainId: ChainId;
  ticker: string;
}) => {
  const pairData = queryClient.getQueryData<Record<string, BareTradePair>>(
    useBackendPairsAll.getQueryKey(chainId),
  );
  const matchedPair = Object.values(pairData ?? {}).find(
    (pair) => pair.ticker === ticker,
  );

  if (!matchedPair?.pythProPriceFeedId) {
    return undefined;
  }

  const config = {
    feedId: matchedPair.pythProPriceFeedId,
    symbol: getPythProHistorySymbol(matchedPair),
  };
  pythProSymbolConfigCache.set(ticker, config);

  return config;
};

const getFirstLoadRangeByResolution = (resolution: string) => {
  const DAY = 86400;

  // Handle common textual resolutions
  const resUpper = resolution.toUpperCase();
  if (resUpper === 'D' || resUpper === '1D') {
    return DAY * 90; // 90 days
  }
  if (resUpper === 'W' || resUpper === '1W') {
    return DAY * 180; // 180 days
  }

  // Fallback: numeric minutes (e.g., '1','5','15','60','240')
  const minutes = Number(resolution);
  if (!Number.isNaN(minutes)) {
    if (minutes <= 2) {
      return DAY * 3; // 1m → 3 days
    }
    if (minutes <= 5) {
      return DAY * 10; // 5m → 10 days
    }
    if (minutes <= 30) {
      return DAY * 15; // 15m/30m → 7 days
    }
    if (minutes <= 60) {
      return DAY * 30; // 1h → 15 days
    }
    if (minutes <= 240) {
      return DAY * 60; // 4h → 30 days
    }
  }

  // Default fallback
  return DAY * 10;
};

// not used yet
export const getVisibleWindowByResolution = (resolution: string) => {
  const DAY = 86400;
  const res = resolution.toUpperCase();
  if (res === 'W' || res === '1W') return DAY * 90; // show last ~3 months on 1W
  if (res === 'D' || res === '1D') return DAY * 60; // show last ~2 months on 1D
  const minutes = Number(resolution);
  if (!Number.isNaN(minutes)) {
    if (minutes <= 5) return DAY * 3; // 1m/5m → last 3 days
    if (minutes <= 30) return DAY * 7; // 15m/30m → last 7 days
    if (minutes <= 60) return DAY * 15; // 1h → last 15 days
    if (minutes <= 240) return DAY * 30; // 4h → last 30 days
  }

  return DAY * 7;
};

export const createDatafeed = ({
  chartId,
  chainId,
}: {
  chainId: ChainId;
  chartId: string;
}) =>
  ({
    setResetData(fn: () => void) {
      resetDataFn = fn;
    },
    onReady: async (callback) => {
      const result = await queryClient.ensureQueryData({
        queryKey: useTradingViewConfig.getQueryKey(),
        queryFn: useTradingViewConfig.getQueryFn(),
      });
      callback({
        ...result,
        supported_resolutions: SUPPORTED_RESOLUTION_STRINGS,
      });
    },
    searchSymbols: () => {
      // not supported
    },
    resolveSymbol: async (
      symbolName,
      onSymbolResolvedCallback,
      onResolveErrorCallback,
    ) => {
      try {
        const pairData = await queryClient.ensureQueryData({
          queryKey: useBackendPairsAll.getQueryKey(chainId),
          queryFn: useBackendPairsAll.getQueryFn(chainId),
        });
        const pairList = Object.values(pairData);
        const matchedPair = pairList.find((v) => v.baseSymbol === symbolName);

        if (!matchedPair) {
          throw new Error(
            `Symbol ${symbolName} is not supported in the current chain`,
          );
        }

        const symbolInfo = await queryClient.ensureQueryData({
          queryKey: useBenchmarkSymbols.getQueryKey({
            ticker: matchedPair.ticker,
          }),
          queryFn: useBenchmarkSymbols.getQueryFn({
            ticker: matchedPair.ticker,
          }),
          staleTime: 1000 * 60 * 60 * 1, // 1 hours
          meta: { persist: true },
        });

        const pricescale = Math.pow(10, matchedPair.priceDisplayDecimals);

        const currentTradePairScheduleFromPyth = matchedPair.schedule;
        const {
          pythSession,
          pythTimezone,
          pythHolidays,
          pythCorrections,
        } = currentTradePairScheduleFromPyth
          ? getPythScheduleInfo(currentTradePairScheduleFromPyth, symbolName)
          : {
              pythSession: undefined,
              pythTimezone: undefined,
              pythHolidays: undefined,
              pythCorrections: undefined,
            };
        const pythProSymbol = getPythProHistorySymbol(matchedPair);

        if (matchedPair.pythProPriceFeedId) {
          pythProSymbolConfigCache.set(matchedPair.ticker, {
            feedId: matchedPair.pythProPriceFeedId,
            symbol: pythProSymbol,
          });
        } else {
          pythProSymbolConfigCache.delete(matchedPair.ticker);
        }

        const resolvedSymbol: LeverUpSymbolInfo = {
          ...(symbolInfo ?? {}),
          // must props
          /**
         * fixme: map pair type to trading view type @SymbolType
         *    | 'stock'
              | 'index'
              | 'forex'
              | 'futures'
              | 'bitcoin'
              | 'crypto'
              | 'undefined'
              | 'expression'
              | 'spread'
              | 'cfd'
              | 'economic'
              | 'equity'
              | 'dr'
              | 'bond'
              | 'right'
              | 'warrant'
              | 'fund'
              | 'structured'
              | 'commodity'
              | 'fundamental'
              | 'spot'
              | 'swap';
         */
          type: matchedPair.type ?? 'crypto',
          listed_exchange: 'LeverUp',
          format: 'price',
          name: symbolName,
          description:
            symbolName === 'JPY' || symbolName === 'CNH'
              ? `USD / ${symbolName}`
              : `${symbolName} / USD`,
          session: pythSession ?? '24x7',
          exchange: 'LeverUp',
          timezone:
            (pythTimezone as Timezone) ?? // first use timezone from BE, which is pyth
            getChartTimezone(),
          pricescale: pricescale,
          minmov: 1,
          supported_resolutions: SUPPORTED_RESOLUTION_STRINGS,

          // extra props
          ticker: matchedPair.ticker,
          session_holidays: pythHolidays,
          corrections: pythCorrections,
          pythProPriceFeedId: matchedPair.pythProPriceFeedId,
          pythProSymbol,
        };

        onSymbolResolvedCallback(resolvedSymbol);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        onResolveErrorCallback(errorMessage);

        return;
      }
    },
    getBars: (
      symbolInfo,
      resolution,
      periodParams,
      onHistoryCallback,
      onErrorCallback,
    ) => {
      const { from, to, firstDataRequest } = periodParams;
      // if () {

      const maxRangeInSeconds = 365 * 24 * 60 * 60; // 1 year in seconds

      let promises: Promise<Bar[]>[] = [];
      let currentFrom = from;
      let currentTo;
      invariant(symbolInfo.ticker, 'Symbol ticker is required');

      const firstLoadRange = getFirstLoadRangeByResolution(resolution);
      // Use a key that only depends on chart + resolution so that switching
      // pairs on the same chart does NOT re-apply the "shrink to N days"
      // logic, and instead respects TradingView's requested from/to.
      const firstRequestKey = `${chartId}_#_${resolution}`;

      // For the *very first* request on a chart+resolution we want to control how
      // much history is loaded (e.g. 3 days for 1m/5m).
      // After that, including when switching pairs or after a stream reconnect,
      // we should respect the periodParams.from/to that correspond to the user's
      // current visible window instead of shrinking it back.
      if (firstDataRequest && !firstRequestHandled.has(firstRequestKey)) {
        currentFrom = Math.min(from, to - firstLoadRange);
        firstRequestHandled.add(firstRequestKey);
      }

      while (currentFrom < to) {
        currentTo = Math.min(to, currentFrom + maxRangeInSeconds);

        const params: GetHistoryParams = {
          ticker: symbolInfo.ticker,
          from: currentFrom,
          to: currentTo,
          resolution: resolution as SupportedResolution,
        };
        const pythProConfig =
          getPythProSymbolConfig(symbolInfo as LeverUpSymbolInfo) ??
          getPythProSymbolConfigFromPairsCache({
            chainId,
            ticker: symbolInfo.ticker,
          });
        const pythProParams: GetPythProHistoryParams | undefined =
          pythProConfig
            ? {
                feedId: pythProConfig.feedId,
                symbol: pythProConfig.symbol,
                from: currentFrom,
                to: currentTo,
                resolution: resolution as SupportedResolution,
              }
            : undefined;
        const queryKey = pythProParams
          ? usePythProHistory.getQueryKey(pythProParams)
          : usePriceHistory.getQueryKey(params);
        const queryFn = pythProParams
          ? async () => {
              try {
                return await usePythProHistory.getQueryFn(pythProParams)();
              } catch (error) {
                console.warn(
                  '[price-chart] Failed to fetch Pyth Pro history, falling back to benchmark history:',
                  error,
                );

                return usePriceHistory.getQueryFn(params)();
              }
            }
          : usePriceHistory.getQueryFn(params);
        promises.push(
          queryClient.ensureQueryData({
            queryKey,
            queryFn,
          }),
        );
        currentFrom = currentTo;
      }

      Promise.all(promises)
        .then((results) => {
          const bars: Bar[] = [];
          results.forEach((data) => {
            data.forEach((bar) => {
              bars.push(bar);
            });
          });

          if (firstDataRequest && bars.length > 0 && symbolInfo.ticker) {
            lastBarsCache.set(
              getBarCacheKey(symbolInfo.ticker, resolution, chartId),
              {
                ...bars[bars.length - 1],
              },
            );
          }

          // For 24x7 sessions, empty data means no more history
          // For non-24x7 sessions (e.g., XAU with trading hours), empty data
          // might just mean a closed period (e.g., Saturday), so we should
          // continue requesting until we reach a reasonable historical limit
          const OLDEST_DATA_TIMESTAMP = 1640995200; // 2022-01-01 UTC
          const is24x7Session = symbolInfo.session === '24x7';
          const isBeforeOldestData = from < OLDEST_DATA_TIMESTAMP;
          const hasNoMoreData =
            bars.length === 0 && (is24x7Session || isBeforeOldestData);

          onHistoryCallback(bars, { noData: hasNoMoreData });
        })
        .catch((error) => {
          onErrorCallback(error);
        });
    },
    subscribeBars: (
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscriberUID,
      onResetCacheNeededCallback,
    ) => {
      if (!isSupportedResolution(resolution)) {
        throw new Error('Invalid resolution');
      }

      // getBar

      subscribeOnStream(
        symbolInfo,
        resolution,
        onRealtimeCallback,
        getUniqSubscriptionId(subscriberUID, chartId),
        () => {
          onResetCacheNeededCallback();
          if (resetDataFn) {
            resetDataFn();
          }
        },
        symbolInfo.ticker
          ? lastBarsCache.get(
              getBarCacheKey(symbolInfo.ticker, resolution, chartId),
            )
          : undefined,
      );
    },
    unsubscribeBars: (subscriberUID) => {
      unsubscribeFromStream(getUniqSubscriptionId(subscriberUID, chartId));
    },
  } satisfies
    | IBasicDataFeed
    | (IDatafeedChartApi &
        IExternalDatafeed &
        IDatafeedQuotesApi & {
          setResetData(fn: () => void): void;
        }));
